import { useMemo, useState } from "react";
import { useField } from "formik";
import CreatableSelect from "react-select/creatable";
import { usePagedTags } from "api/TagResource/useTags";
import InputError from "components/InputError";

const selectStyles = {
	control: (base, state) => ({
		...base,
		minHeight: 42,
		borderRadius: 8,
		borderWidth: 0,
		backgroundColor: "#f3f4f6",
		boxShadow: state.isFocused ? "0 0 0 1px #0f766e" : "none",
		fontSize: "0.875rem",
	}),
	valueContainer: (base) => ({
		...base,
		padding: "4px 12px",
	}),
	multiValue: (base) => ({
		...base,
		backgroundColor: "#ccfbf1",
		borderRadius: 9999,
	}),
	multiValueLabel: (base) => ({
		...base,
		color: "#0f766e",
		fontWeight: 600,
	}),
	multiValueRemove: (base) => ({
		...base,
		color: "#0f766e",
		":hover": {
			backgroundColor: "#99f6e4",
			color: "#115e59",
		},
	}),
	menu: (base) => ({
		...base,
		zIndex: 50,
	}),
};

const TagsField = ({ name = "tags", label = "Tags", fromPortal = "yes" }) => {
	const [field, meta, helpers] = useField(name);
	const [search, setSearch] = useState("");
	const { records, isLoading, isFetching, hasMorePages, loadMore } = usePagedTags({
		fromPortal,
		search,
		rowsPerPage: 40,
	});

	const options = useMemo(
		() =>
			records.map((tag) => ({
				label: tag.name,
				value: tag.name,
			})),
		[records]
	);

	const selected = useMemo(() => {
		const values = Array.isArray(field.value) ? field.value : [];
		return values.map((tagName) => ({
			label: tagName,
			value: tagName,
		}));
	}, [field.value]);

	const handleChange = (selectedOptions) => {
		helpers.setValue((selectedOptions || []).map((option) => option.value));
		helpers.setTouched(true);
	};

	const handleCreate = (inputValue) => {
		const trimmed = inputValue.trim();
		if (!trimmed) return;
		const next = Array.isArray(field.value) ? [...field.value] : [];
		const exists = next.some(
			(tag) => tag.toLowerCase() === trimmed.toLowerCase()
		);
		if (!exists) {
			helpers.setValue([...next, trimmed]);
		}
		helpers.setTouched(true);
	};

	return (
		<div>
			<label htmlFor={name} className="mb-1 block text-sm text-akgreen">
				{label}
			</label>
			<CreatableSelect
				inputId={name}
				isMulti
				isClearable
				isLoading={isLoading || isFetching}
				options={options}
				value={selected}
				onChange={handleChange}
				onCreateOption={handleCreate}
				onInputChange={(value, meta) => {
					if (meta.action === "input-change") setSearch(value);
				}}
				onMenuScrollToBottom={() => {
					if (hasMorePages) loadMore();
				}}
				filterOption={() => true}
				placeholder="Select or type a tag"
				classNamePrefix="tags-select"
				styles={selectStyles}
				formatCreateLabel={(inputValue) => `Create "${inputValue}"`}
				noOptionsMessage={() => (isFetching ? "Loading tags…" : "Type to search or create a tag")}
			/>
			<p className="mt-1 text-xs text-gray-500">Pick from the bank or type to add. Up to 30.</p>
			{meta.error && meta.touched && <InputError error={meta.error} />}
		</div>
	);
};

export default TagsField;
